import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { InfinityMark } from "../components/ConnectMyHubLogo";
import { api } from "../lib/api";
import { Loader2, CheckCircle2, ArrowLeft, ShieldAlert } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [phase, setPhase] = useState("verifying"); // verifying | form | success | invalid
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setPhase("invalid"); return; }
    api.post("/auth/reset-password/verify-token", { token })
      .then(r => setPhase(r.data.valid ? "form" : "invalid"))
      .catch(() => setPhase("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        token, new_password: newPw, confirm_password: confirm,
      });
      setPhase("success");
    } catch (e) {
      setErr(e.response?.data?.detail || "Reset failed. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-lg p-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center"><InfinityMark size={30}/></div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600">Account recovery</div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Reset your password</h1>
          </div>
        </div>

        {phase === "verifying" && (
          <div data-testid="reset-verifying" className="mt-6 text-sm text-slate-500 inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Verifying your reset link…
          </div>
        )}

        {phase === "invalid" && (
          <div data-testid="reset-invalid" className="mt-5 space-y-4">
            <div className="p-4 rounded-lg bg-red-50 border border-red-200 flex gap-3">
              <ShieldAlert size={18} className="text-red-600 shrink-0 mt-0.5"/>
              <div>
                <div className="text-sm font-semibold text-red-900">Link invalid or expired</div>
                <p className="text-xs text-red-800 mt-1">
                  This password reset link is invalid or has expired. Please request a new one.
                </p>
              </div>
            </div>
            <Link to="/forgot-password" data-testid="request-another-reset"
                  className="block w-full text-center h-10 leading-10 rounded-md bg-slate-900 text-white text-sm font-semibold">
              Request a new reset link
            </Link>
          </div>
        )}

        {phase === "form" && (
          <>
            <p className="text-sm text-slate-500 mt-3 mb-5">
              Choose a new password. It must be at least 10 characters and include letters and at least one digit.
            </p>
            <form onSubmit={submit} data-testid="reset-form">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">New password</label>
              <input type="password" required autoFocus autoComplete="new-password"
                      data-testid="reset-new-password" value={newPw} onChange={e=>setNewPw(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:border-sky-500 outline-none mb-3"/>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Confirm new password</label>
              <input type="password" required autoComplete="new-password"
                      data-testid="reset-confirm-password" value={confirm} onChange={e=>setConfirm(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:border-sky-500 outline-none mb-3"/>
              {err && <div data-testid="reset-error" className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
              <button data-testid="reset-submit" disabled={busy}
                      className="w-full h-10 rounded-md bg-slate-900 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {busy && <Loader2 size={14} className="animate-spin"/>} Reset password
              </button>
            </form>
          </>
        )}

        {phase === "success" && (
          <div className="mt-5 space-y-4" data-testid="reset-success">
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 flex gap-3">
              <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5"/>
              <div>
                <div className="text-sm font-semibold text-emerald-900">Password updated</div>
                <p className="text-xs text-emerald-800 mt-1">
                  Your ConnectMyHub password has been reset successfully.
                </p>
              </div>
            </div>
            <button data-testid="reset-success-signin" onClick={()=>nav("/login")}
                    className="w-full h-10 rounded-md bg-slate-900 text-white text-sm font-semibold">
              Sign in
            </button>
          </div>
        )}

        {phase !== "success" && (
          <div className="mt-6 pt-5 border-t border-slate-200 text-center">
            <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900">
              <ArrowLeft size={12}/> Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
